import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import {
    FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    FlightDto,
    FlightListByTvRequestDto,
    FlightListDto,
    Reply,
    ScenarioAttributesDto,
    TimePeriod,
    TimeWindowType,
    TrafficType,
} from '../../../../model/CORE';
import { ServiceFactory } from '../../../../service/ServiceFactory';
import AppContext from '../../../../common/context/AppContext';
import { Button, notification, Tabs } from 'antd';
import ScenarioDescription from './components/ScenarioDescription';
import { FLIGHT_SELECTED_COLUMN, FlightColumn, StandardFlightColumn } from '../../../table/FlightTable';
import { PanelDisplayMode, ScenarioSimulation } from '../ApplicableScenariosPanel';
import SimulationResultDetails from './components/SimulationResultDetails';
import { DetailedSolution, ReroutedFlightDto } from '../../../../model/SOL49';

export interface ScenarioTvFlightsPanelProps {
    selectedScenario: ScenarioAttributesDto;
    selectedTimePeriod: TimePeriod;
    onSelectFlights: (flights: FlightDto[]) => void;
    onRequestSimulation: (scenario: ScenarioAttributesDto, simulationName: string) => void;
    simulations: Array<ScenarioSimulation>;
    selectedSimulation: ScenarioSimulation;
    onSelectSimulation: (simulation: ScenarioSimulation) => void;
    solverSolutions: Array<DetailedSolution>;
    solverOnShowTVsImpact: () => void;
    solverOnShowMeasuresImpact?: () => void;
    solverOnShowOnMap: () => void;
    solverOnRrtFlightShowOnMap: (flights: ReroutedFlightDto[]) => void;
}

export interface ScenarioTvFlightsPanelState {
    flightList: FlightDto[];
    selectedFlights: FlightDto[];
    isFlightListLoading: boolean;
    displayMode: PanelDisplayMode;
}

export default class ScenarioInformationPanel extends PureComponent<
    ScenarioTvFlightsPanelProps,
    ScenarioTvFlightsPanelState
> {
    constructor(props: ScenarioTvFlightsPanelProps) {
        super(props);
        this.state = {
            flightList: [],
            selectedFlights: [],
            isFlightListLoading: true,
            displayMode: PanelDisplayMode.SCENARIO,
        };
    }

    componentDidMount() {
        this.requestScenarioFlightList(this.props.selectedScenario);
    }

    componentDidUpdate(prevProps: Readonly<ScenarioTvFlightsPanelProps>) {
        if (
            prevProps.selectedScenario?.scenarioId !== this.props.selectedScenario.scenarioId ||
            prevProps.selectedTimePeriod !== this.props.selectedTimePeriod
        ) {
            this.requestScenarioFlightList(this.props.selectedScenario);
        }
        if (!this.props.selectedSimulation) {
            this.setState({
                displayMode: PanelDisplayMode.SCENARIO,
            });
        }
    }

    SIMPLE_FLIGHT_COLUMNS: FlightColumn[] = [
        FLIGHT_SELECTED_COLUMN,
        StandardFlightColumn.ENTRY_TIME,
        StandardFlightColumn.EXIT_TIME,
        StandardFlightColumn.ARCID,
        StandardFlightColumn.ATYPE,
        StandardFlightColumn.ADEP,
        StandardFlightColumn.ADES,
        StandardFlightColumn.CFMU_TYPE,
        StandardFlightColumn.ATOT_TTOT,
        StandardFlightColumn.CTOT,
        StandardFlightColumn.ATOT,
        StandardFlightColumn.xTA,
        StandardFlightColumn.MPR,
        StandardFlightColumn.DELAY,
        StandardFlightColumn.CONVECTION_IMPACT,
    ];

    private requestScenarioFlightList(scenario: ScenarioAttributesDto) {
        this.setState({
            flightList: [],
            selectedFlights: [],
            isFlightListLoading: true,
        });
        if (scenario.scenarioPublishedTrafficVolumeIds.length === 0) {
            console.warn('No TV associated with the Scenario');
        } else if (scenario.scenarioPublishedTrafficVolumeIds.length <= 3) {
            let tvToSearch = scenario.scenarioPublishedTrafficVolumeIds;
            if (
                scenario.scenarioPublishedTrafficVolumeIds.filter(value => value.toUpperCase().startsWith('GR'))
                    .length > 0 &&
                scenario.scenarioPublishedTrafficVolumeIds.filter(value => value.toUpperCase().startsWith('AW'))
                    .length > 0
            ) {
                tvToSearch = tvToSearch.filter(value => !value.toUpperCase().startsWith('AW'));
            }
            const flightRequests: Promise<Reply<FlightListDto>>[] = tvToSearch.map(publishedTrafficVolumeId => {
                const flightListRequest: FlightListByTvRequestDto = {
                    clazz: FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
                    fields: FlightColumn.computeB2BFieldList(this.SIMPLE_FLIGHT_COLUMNS),
                    dataset: AppContext.getDataset(),
                    timeType: TimeWindowType.OCCUPANCY,
                    tvName: publishedTrafficVolumeId,
                    trafficType: TrafficType.LOAD,
                    trafficWindow: this.props.selectedTimePeriod,
                };
                return ServiceFactory.coreService().getFlightList(flightListRequest);
            });
            Promise.all(flightRequests)
                .then(replies => {
                    if (replies.find(reply => reply.error !== undefined)) {
                        throw new Error(replies.find(reply => reply.error !== undefined).error.message);
                    } else {
                        this.setState({
                            flightList: replies
                                .map(flightRequest => flightRequest.data.flights)
                                .reduce((accumulator, currentValue) => accumulator.concat(currentValue), []),
                            isFlightListLoading: false,
                        });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        } else {
            console.warn(
                'Risky Call if too many requests are sent to B2B: ' + scenario.scenarioPublishedTrafficVolumeIds.length,
            );
            notification.error({
                message: 'Error',
                description:
                    'Risky Call if too many requests are sent to B2B: ' +
                    scenario.scenarioPublishedTrafficVolumeIds.length,
                duration: 0,
            });
        }
    }

    private onRequestSimulation = () => {
        const simulationName = 'Simulation ' + (this.props.simulations ? this.props.simulations.length + 1 : 1);
        this.props.onRequestSimulation(this.props.selectedScenario, simulationName);
    };

    private onFlightsSelected = (flights: FlightDto[]) => {
        this.setState({ selectedFlights: flights });
        this.props.onSelectFlights(flights);
    };

    private onTabChange = (tabKey: string) => {
        if (tabKey === 'description') {
            this.props.onSelectSimulation(null);
        } else {
            const scenarioSimulationStates = this.props.simulations.filter(
                simulationState => simulationState.simulationName === tabKey,
            );
            if (scenarioSimulationStates && scenarioSimulationStates.length === 1) {
                this.props.onSelectSimulation(scenarioSimulationStates[0]);
            } else {
                console.error('No simulation has name: ' + tabKey);
            }
        }
    };

    private renderPanelTabs() {
        const items = [
            {
                label: 'Description',
                key: 'description',
            },
        ];
        if (this.props.simulations) {
            this.props.simulations.forEach(simulationState => {
                items.push({
                    label: simulationState.simulationName,
                    key: simulationState.simulationName,
                });
            });
        }
        if (this.props.simulations && this.props.simulations.length > 0) {
            return (
                <Flex direction={'horizontal'} grow={false}>
                    <Flex grow={false}>
                        <Tabs
                            items={items}
                            defaultActiveKey="description"
                            activeKey={
                                this.props.selectedSimulation
                                    ? this.props.selectedSimulation.simulationName
                                    : 'description'
                            }
                            type="card"
                            onChange={this.onTabChange}
                        />
                    </Flex>
                    <Flex grow={false} style={{ maxHeight: '20px' }}>
                        <Button className={'run-simulation'} onClick={this.onRequestSimulation}>
                            + Run Simulation
                        </Button>
                    </Flex>
                </Flex>
            );
        } else {
            return (
                <Flex direction={'horizontal'} grow={false}>
                    <Flex grow={false} style={{ maxHeight: '20px', marginLeft: '2em' }}>
                        <Button className={'run-simulation'} onClick={this.onRequestSimulation}>
                            + Run Simulation
                        </Button>
                    </Flex>
                </Flex>
            );
        }
    }

    render() {
        return (
            <Flex direction="vertical" className={'applicable-scenarios-sub-panel'}>
                <Flex direction={'horizontal'} grow={false}>
                    <Flex
                        size="2em"
                        style={{ fontSize: '1.5em', fontWeight: 'bold', padding: '10px 0 10px 10px' }}
                        grow={false}>
                        {this.props.selectedScenario.publicationId}
                    </Flex>
                    {this.renderPanelTabs()}
                </Flex>
                {!this.props.selectedSimulation && (
                    <ScenarioDescription
                        selectedScenario={this.props.selectedScenario}
                        flightList={this.state.flightList}
                        isFlightListLoading={this.state.isFlightListLoading}
                        selectedFlights={this.state.selectedFlights}
                        onFLightsSelected={this.onFlightsSelected}
                    />
                )}
                {this.props.selectedSimulation && (
                    <SimulationResultDetails
                        simulationState={this.props.selectedSimulation}
                        solverSolutions={this.props.solverSolutions}
                        onShowTVsImpact={this.props.solverOnShowTVsImpact}
                        onShowMeasuresImpact={this.props.solverOnShowMeasuresImpact}
                        onShowOnMap={this.props.solverOnShowOnMap}
                        onRrtFlightShowOnMap={this.props.solverOnRrtFlightShowOnMap}
                    />
                )}
            </Flex>
        );
    }
}
